/* eslint-disable camelcase */
import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { useSelector } from 'react-redux';
import { breakpoints } from 'axl-config';
import { Hero } from 'reaxl-organisms';
import Head from 'next/head';
import { useDevice } from '@bonnet/next/device';
import { adBlockDetect } from 'atc-js';
import { Link, PositionedOverlay } from 'reaxl';
import { useOptimizelyTrack } from 'reaxl-optimizely';
import { useSwipeable } from 'react-swipeable';
import { sendClick, sendImpressions } from 'reaxl-analytics';
import { linksPreload, useWindowMessageEvent } from '@/utilities';
import { handleHomePageKeywordSearchSubmit } from '@/actions/heroSearchActions';
import { BrowseByLinks, SearchBadge, DotIndicator } from '@/component';
import { callToActionAnalytics } from '@/analytics';

import { SuperHeroAdBarEnhanced } from '.';
import { recentSearchDuck } from '@/ducks';
import renderPlayButton from '@/component/Shared/renderPlayButton';
import HeaderClientContent from './SuperHeroHeaderClientContent';
import HeaderEditorialContent from './SuperHeroHeaderEditorialContent';
import { superHeroContent as SuperHeroRefrence } from '@/reference';

const getEnhancedHeroVideoButtonSrc = (mediaType) => {
    switch (mediaType) {
        case 'xs':
            return SuperHeroRefrence.playButtonImage.small;
        default:
            return SuperHeroRefrence.playButtonImage.large;
    }
};

const getSuperHeroPreloadSrc = (mediaType, bodyText) => {
    const { image, imageSrc, imageSm, imageMd } = bodyText;

    switch (mediaType) {
        case 'xs': return imageSrc;
        case 'sm': return imageSm;
        case 'md': return imageMd;
        default: return image;
    }
};

const getSecondaryLinkUrl = (gamBodyText, currentSlideIndex) => {
    if (gamBodyText && gamBodyText.at(currentSlideIndex)?.content_select === 'editorial_content') {
        return gamBodyText.at(currentSlideIndex)?.secondary_link_url;
    }
    return undefined;
};

const commonHotStyles = 'super-hero-image-hot-area';
const homeHotStyles = 'super-hero-image-home-hot-area';
const slpHotStyles = 'super-hero-image-slp-hot-area';

const preloadImg = (imgSrc, logo, videoButton) => (
    <Head>
        {imgSrc && linksPreload.preImage(imgSrc)}
        {logo && linksPreload.preImage(logo)}
        {videoButton && linksPreload.preImage(videoButton)}
        <style type="text/css">
            {`
            .${homeHotStyles} {
              top: 215px;
            }

            @media screen and (min-width: ${breakpoints.min.sm}px) {
              .${homeHotStyles} {
                top: 145px;
              }
            }
          `}
        </style>
    </Head>
);

const SuperHeroCarousel = React.memo(({
    isHomepage,
    superHeroData,
    userZipCode,
    styleCodes,
    heroSlotId,
    headingOneTitle,
    pageData,
    originPage,
    dealerId,
}) => {
    const optimizelyTrack = useOptimizelyTrack();

    const device = useDevice();
    const mediaType = device?.mediaType;
    const isMobile = device?.is.xs;

    const { makeCode = '', modelCode = '' } = useSelector(recentSearchDuck.selectors.getRecentSearchQuery);

    // AdBlock state
    const [adBlockerDetected, setAdBlockerDetected] = useState(false);

    // Carousel state
    const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
    const [superHeroContent, setSuperHeroContent] = useState(superHeroData[currentSlideIndex]);
    const [stopRotaion, setStopRotation] = useState(false);
    const slidesIntervalRef = useRef();

    const lastSlideIndex = superHeroData.length - 1;
    const isMultiSlide = superHeroData.length > 1;
    const hasOneSlide = superHeroContent?.richMediaType === 'animated_header';
    const isAnimatedHeader = hasOneSlide && superHeroContent?.animation;
    const hasClickableImage = hasOneSlide || !isMobile;

    // SuperHeroGamData
    let superHeroGamData = useWindowMessageEvent(heroSlotId);
    const isSuperHeroFallback = superHeroContent && Object.prototype.hasOwnProperty.call(superHeroContent, 'useFallback');
    superHeroGamData = isSuperHeroFallback ? superHeroContent.fallbackGamData : superHeroGamData;

    const listingType = isHomepage ? 'used' : pageData && pageData.pageValue;

    const playButtonStyle = {
        bottom: isHomepage || isMobile ? '0px' : '50px',
    };

    const handleAutoSlide = ({ force = false } = {}) => {
        if ((force || !slidesIntervalRef.current) && !stopRotaion) {
            const intervalId = setInterval(() => {
                setCurrentSlideIndex((prevCount) => {
                // done with first rotation
                    if (prevCount + 1 > lastSlideIndex) {
                        setStopRotation(true);
                        return 0;
                    }

                    // advance to next slide after 4sec
                    return prevCount + 1;
                });
            }, 4000);

            slidesIntervalRef.current = intervalId;
        }
    };

    // Handle SuperHero Data according to currentSlideIndex
    useEffect(() => {
        setSuperHeroContent(superHeroData[currentSlideIndex]);
    }, [currentSlideIndex]);

    // Auto Advance Slides
    useEffect(() => {
        if (!hasOneSlide) {
            handleAutoSlide();
        }
        return () => clearInterval(slidesIntervalRef.current);
    }, []);

    // stop the auto-advance
    useEffect(() => {
        if (currentSlideIndex === lastSlideIndex && stopRotaion) {
            clearInterval(slidesIntervalRef.current);
        }
    }, [currentSlideIndex]);

    // Update only once unless that state of adblocker changes
    useEffect(() => {
        adBlockDetect().then((bool) => setAdBlockerDetected(bool));
    }, [adBlockerDetected]);

    // ------------- ANIMATIONS -------------
    const [isMounted, setIsMounted] = useState(false);
    const [isHeaderAnimated, setIsHeaderAnimated] = useState(isAnimatedHeader);
    const [isTextFadeIn, setIsTextFadeIn] = useState(false);
    const [isImageFadeIn, setIsImageFadeIn] = useState(false);

    useEffect(() => {
        const richMediaType = superHeroContent?.richMediaType;

        const config = {
            animated_header: {
                isHeaderAnimated: true,
                isTextFadeIn: false,
                isImageFadeIn: false,
            },
            multi_header: {
                isHeaderAnimated: false,
                isTextFadeIn: true,
                isImageFadeIn: false,
            },
            multi_image: {
                isHeaderAnimated: false,
                isTextFadeIn: false,
                isImageFadeIn: true,
            },
            multi_image_header: {
                isHeaderAnimated: false,
                isTextFadeIn: true,
                isImageFadeIn: true,
            },
        };
        if (isMounted) {
            setIsHeaderAnimated(config[richMediaType]?.isHeaderAnimated);
            setIsTextFadeIn(config[richMediaType]?.isTextFadeIn);
            setIsImageFadeIn(config[richMediaType]?.isImageFadeIn);
        } else {
            setIsMounted(true);
        }
    }, [currentSlideIndex]);

    // ------------- ACCESSIBILITY -------------
    const goForward = () => {
        clearInterval(slidesIntervalRef.current);
        setCurrentSlideIndex((prevCount) => {
            if (prevCount === lastSlideIndex) {
                return 0;
            }
            return prevCount + 1;
        });
    };

    const goBack = () => {
        clearInterval(slidesIntervalRef.current);
        setCurrentSlideIndex((prevCount) => {
            if (prevCount === 0) {
                return lastSlideIndex;
            }
            return prevCount - 1;
        });
    };

    const handleKeyPress = (event) => {
        const pressedKey = event.key;

        if (!['Escape', 'ArrowLeft', 'ArrowRight'].includes(pressedKey)) return;

        if (pressedKey === 'ArrowRight') {
            goForward();
        } else if (pressedKey === 'ArrowLeft') {
            goBack();
        }
    };

    useEffect(() => {
        window.addEventListener('keydown', handleKeyPress);
        return () => {
            window.removeEventListener('keydown', handleKeyPress);
        };
    }, [handleKeyPress]);

    // ------------- LEFT-RIGHT SWIPE  -------------
    const swipeableHandlers = useSwipeable({
        onSwipedLeft: () => {
            if (isMobile) goForward();
        },
        onSwipedRight: () => {
            if (isMobile) goBack();
        },
    });

    const handleKeywordSearchSubmitCallback = (data = {}) => {
        optimizelyTrack('hp_search_click');
        handleHomePageKeywordSearchSubmit({ ...data });
    };

    const handleDotIndicatorClick = (i) => {
        setStopRotation(true);
        clearInterval(slidesIntervalRef.current);
        setCurrentSlideIndex(i);
    };

    const onClickPixallHandler = (event, label) => {
        sendClick('enhancedHeroCtaClick', event, {
            label,
            hasListingType: true,
        });
    };

    const handleMouseEnter = () => {
        clearInterval(slidesIntervalRef.current);
    };

    const handleMouseLeave = () => {
        handleAutoSlide({ force: true });
    };

    const {
        makeModelSearchLink,
        ctaText,
        makeName,
        modelName,
        makeUrl,
        modelUrl,
        year,
        id,
        wpVideoUrl,
        wpVideoThumb,
        wpVideoMediaId, // eslint-disable-line
        wpVideoName,
        logo,
        bodyText,
    } = superHeroContent;

    const { image, imageMd, imageSm, imageSrc } = bodyText;

    const formattedCtaText = ctaText?.replace(/\s+/g, '-').toLowerCase();

    const contentType = bodyText?.contentSelect;

    const { destination_url, destination_url_is_external, body_text = [] } = superHeroGamData || {};

    const themedTextColorClass = clsx({
        'text-inverse': true,
    });

    // get optimal image src
    const superHeroPreloadSrc = getSuperHeroPreloadSrc(mediaType, bodyText);
    const enhancedSuperHeroVideoButtonSrc = getEnhancedHeroVideoButtonSrc(mediaType);

    const withMakeModelSearchLinks = makeModelSearchLink && makeName && modelName && makeUrl && modelUrl;
    const heroStyleContext = 'superhero';
    const repositionHeroImage = !isHomepage || (isHomepage && !isSuperHeroFallback);
    const hasVideoHeader = contentType === 'video_content' && bodyText.header;

    // Video player
    const ctaOptions = {
        destinationUrl: destination_url,
        ctaText,
        ctaOnClick: (e) => {
            callToActionAnalytics.fireHeroAdCTAClick(e, {
                label: ctaText,
                catalog: 'atc',
                type: 'cta',
                cmp: 'mmt_btn',
                makeId: makeCode,
                makeName,
                modelId: modelCode,
                modelName,
                year,
            },);
        },
        ctaOnMediaEvent: (e) => {
            if (e.type === 'click') {
                setStopRotation(true);
                sendClick('enhancedHeroCtaClick', e, {
                    label: `video::hero-ad::${e.eventLabel}`,
                });
            } else {
                sendImpressions({
                    name: 'enhancedHeroImpression',
                    data: {
                        label: `video::hero-ad::${e.eventLabel}`,
                    },
                });
            }
        },
    };

    return (
        <>
            {preloadImg(superHeroPreloadSrc, logo, enhancedSuperHeroVideoButtonSrc)}
            <div
                {...swipeableHandlers}
                className="enhanced-hero-container"
            >
                <Hero
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                    uiContext={isHomepage ? heroStyleContext : 'billboard-search'}
                    contentPosition="topLeft"
                    className={`${repositionHeroImage ? 'super-hero-image' : ''} ${isImageFadeIn ? 'super-hero-image-fade-in-animation' : ''} transparent-header`}
                    block
                    src={image}
                    lgSrc={image}
                    mdSrc={imageMd}
                    smSrc={imageSm}
                    xsSrc={imageSrc}
                    isSponsoredByTextWhite
                    data-cmp="hero"
                >
                    <SearchBadge
                        className={`col-sm-12 col-md-10 col-md-offset-1 margin-top-4 ${themedTextColorClass} padding-left-3 padding-right-3`}
                        badgeTitle={headingOneTitle}
                        labelClassName={themedTextColorClass}
                        titleClassName="text-bold text-size-500"
                        makeConfig={{
                            analyticsData: { pageType: 'homepage' },
                            //! first need to add make and model to API call
                            help: isHomepage && withMakeModelSearchLinks && (
                                <Link
                                    data-cmp="makeSearchLink"
                                    href={makeUrl}
                                    className={`text-size-200 ${themedTextColorClass}`}
                                    onClick={(event) => callToActionAnalytics.fireMakeExampleLinkClick(event, makeName)}
                                >
                                    {`Ex: ${makeName}`}
                                </Link>
                            ),
                        }}
                        modelConfig={{
                            analyticsData: { pageType: 'homepage' },
                            help: isHomepage && withMakeModelSearchLinks && (
                                <Link
                                    data-cmp="modelSearchLink"
                                    href={modelUrl}
                                    className={`text-size-200 ${themedTextColorClass}`}
                                    onClick={(event) => callToActionAnalytics.fireModelExampleLinkClick(event, modelName)}
                                >
                                    {`Ex: ${modelName}`}
                                </Link>
                            ),
                        }}
                        onKeywordSearchFormSubmit={handleKeywordSearchSubmitCallback}
                        browseByLinks={(
                            <BrowseByLinks
                                enableBrowseByBudgetLink={isHomepage}
                                classNames={themedTextColorClass}
                                listingType={listingType}
                                styleCodes={styleCodes}
                                analyticsPageType={isHomepage ? 'homepage' : `${listingType}-car`}
                                zipCode={userZipCode}
                                dealerId={dealerId}
                            />
                        )}
                    />
                    <PositionedOverlay
                        className={`super-hero-overlay ${isTextFadeIn && 'enhanced-super-hero-header-fade-in-animation'} ${isHeaderAnimated && 'enhanced-super-hero-header-animation'}`}
                        key={isTextFadeIn && currentSlideIndex}
                    >
                        {contentType === 'editorial_content' && (
                            <HeaderEditorialContent
                                isHomepage={isHomepage}
                                isCarousel
                                superHeroGamData={superHeroGamData}
                                superHeroContent={superHeroContent}
                                secondaryLinkUrl={getSecondaryLinkUrl(body_text, currentSlideIndex)}
                                handleReadMoreClick={(event) => onClickPixallHandler(event, `text-link::hero-ad::read-more::slide_${currentSlideIndex}`)}
                            />
                        )}
                        {(contentType === 'client_provided_content' || hasVideoHeader) && (
                            <HeaderClientContent
                                isHomepage={isHomepage}
                                superHeroContent={superHeroContent}
                            />
                        )}
                        {contentType === 'video_content' && (
                            renderPlayButton({
                                video: { id, name: wpVideoName, src: wpVideoUrl, thumbnail: wpVideoThumb }, // Wordpress video object
                                playButtonStyle,
                                ctaOptions,
                                autoStart: true,
                                withCustomImageButtonSrc: enhancedSuperHeroVideoButtonSrc,
                                originPage,
                                isEnhancedHero: true,
                                className: isHomepage ? 'hp' : 'slp',
                            })
                        )}
                        {hasClickableImage && (
                            <Link
                                data-cmp="heroImageClickableArea"
                                className={`${commonHotStyles} ${isHomepage ? homeHotStyles : slpHotStyles}`}
                                rel={destination_url_is_external && 'nofollow noopener'}
                                target={destination_url_is_external ? '_blank' : '_self'}
                                href={destination_url}
                                tabIndex="-1"
                                onClick={(event) => {
                                    onClickPixallHandler(event, 'image::hero-ad');
                                }}
                            />
                        )}
                    </PositionedOverlay>

                    <SuperHeroAdBarEnhanced
                        isHomepage={isHomepage}
                        isCarousel
                        themedTextColorClass={themedTextColorClass}
                        superHeroContent={superHeroContent}
                        isMobile={isMobile}
                        destinationUrlIsExternal={destination_url_is_external}
                        destinationUrl={destination_url}
                        isEnhanced
                        handleLogoClick={(event) => onClickPixallHandler(event, `logo::hero-ad::logo::slide_${currentSlideIndex}`)}
                        handleButtonClick={(event) => onClickPixallHandler(event, `cta::hero-ad::${formattedCtaText}::slide_${currentSlideIndex}`)}
                        stopCarousel={setStopRotation}
                    >
                        {isMultiSlide && (
                            <DotIndicator
                                clickToSlide={(e) => {
                                    sendClick('enhancedHeroCtaClick', e, {
                                        label: 'selection::hero-ad::slide',
                                    });
                                }}
                                loadSlide={(e) => {
                                    sendImpressions({
                                        name: 'enhancedHeroImpression',
                                        data: {
                                            label: `selection::hero-ad::slide_${currentSlideIndex}`,
                                        },
                                    });
                                }}
                                onDotIndicatorClick={handleDotIndicatorClick}
                                currentSlideIndex={currentSlideIndex}
                                totalSlides={superHeroData.length}
                                isMobile={isMobile}
                            />
                        )}
                    </SuperHeroAdBarEnhanced>
                </Hero>
            </div>
        </>
    );
});

SuperHeroCarousel.propTypes = {
    className: PropTypes.string,
    heroAd: PropTypes.object,
    gradient: PropTypes.bool,
    children: PropTypes.node,
    makeCode: PropTypes.string,
    modelCode: PropTypes.string,
    dealerId: PropTypes.string,
};

export default SuperHeroCarousel;
